import { EdgeId, EdgeModel } from '../../model/edge.model';
import { NodeId, NodeModel, NodeType } from '../../model/node.model';
import { DiagramState } from '../diagram.store';
import {
  applyEdgeChanges,
  applyNodeChanges,
  EdgeChange,
  NodeChange,
} from '@xyflow/react';
import { ElementById, GetState, ResizePatch, SetState } from '../store.types';
import { v4 as uuidv4 } from 'uuid';
import { makeNode } from '../../model/node.factory';
import { ChangeModel, Status } from '../../model/change.model';
import { DrillDownModel } from '@diagram/model/drilldown.model';
import { toArrayInOrder } from '../store.utils';
import { DrillDownId } from '../../model/drilldown.model';
// handles local state for react flow nodes and edges
export interface RfSlice {
  nodesById: ElementById<NodeModel>;
  edgesById: ElementById<EdgeModel>;
  drilldowns: ElementById<DrillDownModel>;

  nodeIndex: number;

  activeDrilldown: DrillDownId;
  activeCategory: string;

  rfNodes: NodeModel[];
  rfEdges: EdgeModel[];

  createNodeAt(input: {
    type: NodeType;
    shape?: string;
    orientation?: string;
    x: number;
    y: number;
    width?: number;
    height?: number;
  }): NodeId;

  // uses allocated order
  addNodes(nodes: NodeModel[]): void;
  addEdges(edges: EdgeModel[]): void;

  removeNodes(ids: NodeId[]): void;
  removeEdges(ids: EdgeId[]): void;
  setNodes(nodes: NodeModel[]): void;
  patchNodes: (
    patches: Array<{ id: NodeId; patch: Partial<NodeModel> | null }>
  ) => void;
  patchResizeNodes(patches: ResizePatch[]): void;
  setEdges(edges: EdgeModel[]): void;
  patchEdges: (
    patches: Array<{ id: EdgeId; patch: Partial<EdgeModel> | null }>
  ) => void;

  patchDrilldowns: (
    patches: Array<{
      id: DrillDownId;
      patch: Partial<DrillDownModel> | null;
    }>
  ) => void;

  // Handler for react flow node changes
  applyOperationChanges: (changes: ChangeModel[]) => void;
  applyNodesChanges: (changes: NodeChange[]) => void;
  applyEdgesChanges: (changes: EdgeChange[]) => void;
}

// const toMap = (arr: NodeModel[] | EdgeModel[]): ElementById<NodeModel> => {
//   const m: ElementById<NodeModel> = Object.create(null);
//   for (const n of arr as NodeModel[]) {
//     m[n.id as NodeId] = n;
//   }
//   return m;
// };

export const createRfSlice = (set: SetState, get: GetState) =>
  ({
    nodesById: {},
    edgesById: {},
    drilldowns: {},
    rfNodes: [],
    rfEdges: [],

    nodeIndex: 0,

    activeCategory: '',
    activeDrilldown: '' as DrillDownId,
    createNodeAt: (input) => {
      const { type, x, y } = input;
      const id = uuidv4() as NodeId;

      const node = makeNode({
        id,
        type,
        x,
        y,
        width: input.width,
        height: input.height,
        data: {
          type,
          label: 'New Node',
          color: '#000000',
          index: get().nodeIndex,
          ...(input.shape && { shape: input.shape }),
          ...(input.orientation && { orientation: input.orientation }),
        } as NodeModel['data'],
      });
      set((s: DiagramState) => {
        s.recordOperation([
          {
            type: 'create:node',
            status: 'start',
            id: node.id as NodeId,
            data: node,
          } as ChangeModel,
        ]);

        s.sendIndex();
        return {
          rfNodes: [...s.rfNodes, node],
          nodesById: { ...s.nodesById, [id]: node },
        };
      });
      return id;
    },

    addNodes: (nodes) => {
      if (!nodes.length) return;
      set((s) => {
        const newNodesIds = nodes.reduce((acc, n) => {
          {
            acc[n.id] = {
              ...n,
              measured: { width: n.width, height: n.height },
            };
            return acc;
          }
        }, {} as ElementById<NodeModel>);

        const temp = { ...s.nodesById, ...newNodesIds };
        const tempArray = [...s.rfNodes, ...nodes];

        return {
          nodesById: temp,
          rfNodes: tempArray,
        };
      });
    },

    addEdges: (edges) => {
      if (!edges.length) return;
      set((s) => {
        const edgesById = { ...s.edgesById } as Record<EdgeId, EdgeModel>;
        for (const e of edges) {
          edgesById[e.id as EdgeId] = e;
        }
        const rfEdges = Object.values(edgesById);
        return { edgesById, rfEdges };
      });
    },

    removeNodes: (ids) => {
      if (!ids.length) return;
      set((s) => {
        const nodesById = { ...s.nodesById } as ElementById<NodeModel>;
        for (const id of ids) {
          delete nodesById[id];
        }
        const rfNodes = toArrayInOrder(nodesById);
        return { nodesById, rfNodes };
      });
    },

    removeEdges: (ids) => {
      if (!ids.length) return;
      set((s) => {
        const edgesById = { ...s.edgesById } as Record<EdgeId, EdgeModel>;
        for (const id of ids) {
          delete edgesById[id];
        }
        const rfEdges = Object.values(edgesById);
        return { edgesById, rfEdges };
      });
    },

    setNodes: (nodes) => {
      const newNodesById = nodes.reduce((acc, n) => {
        acc[n.id as NodeId] = n;
        return acc;
      }, {} as ElementById<NodeModel>);
      set(() => ({
        nodesById: newNodesById,
        rfNodes: nodes,
      }));
    },
    patchNodes: (patches) => {
      set((s) => {
        const nodesById = { ...s.nodesById } as ElementById<NodeModel>;
        for (const { id, patch } of patches) {
          if (patch !== null) {
            const node = nodesById[id as NodeId] || {};
            const updatedNode = {
              ...node,
              ...patch,
              data: { ...node.data, ...patch.data },
            };
            nodesById[id as NodeId] = updatedNode as NodeModel;
          } else {
            delete nodesById[id as NodeId];
          }
        }
        const rfNodes = toArrayInOrder(nodesById);
        return { nodesById, rfNodes };
      });
    },

    patchResizeNodes: (patches) => {
      set((s) => {
        const nodesById = s.nodesById as ElementById<NodeModel>;
        for (const { id, x, y, width, height } of patches) {
          const node = nodesById[id as NodeId];
          if (!node) continue;
          // apply only changed position / size
          const updatedNode = {
            ...node,
            measured: {
              width: width ?? node.width,
              height: height ?? node.height,
            },
            position: {
              x: x ?? node.position.x,
              y: y ?? node.position.y,
            },
            width: width ?? node.width,
            height: height ?? node.height,
          };
          nodesById[id as NodeId] = updatedNode as NodeModel;
        }
        const rfNodes = toArrayInOrder(nodesById);
        return { nodesById, rfNodes };
      });
    },

    setEdges: (edges) => {
      const newEdgesById = edges.reduce((acc, e) => {
        acc[e.id as EdgeId] = e;
        return acc;
      }, {} as Record<EdgeId, EdgeModel>);
      set(() => ({
        edgesById: newEdgesById,
        rfEdges: edges,
      }));
    },
    patchEdges: (patches) => {
      set((s) => {
        const edgesById = { ...s.edgesById } as Record<EdgeId, EdgeModel>;
        for (const { id, patch } of patches) {
          if (patch === null) {
            delete edgesById[id as EdgeId];
            continue;
          }
          const edge = edgesById[id as EdgeId] || {};
          const updatedEdge = { ...edge, ...patch };
          edgesById[id as EdgeId] = updatedEdge;
        }
        const rfEdges = Object.values(edgesById);
        return { edgesById, rfEdges };
      });
    },

    patchDrilldowns: (patches) => {
      set((s) => {
        const drilldowns = { ...s.drilldowns } as ElementById<DrillDownModel>;
        for (const { id, patch } of patches) {
          if (patch !== null) {
            const drilldown = drilldowns[id] || {};
            const updatedDrilldown = { ...drilldown, ...patch };
            drilldowns[id] = updatedDrilldown;
          } else {
            delete drilldowns[id];
          }
        }
        return { drilldowns };
      });
    },

    applyOperationChanges: (changes) => {
      const nodeChanges: NodeChange[] = [];
      const nodePatches: Array<{ id: NodeId; patch: Partial<NodeModel> }> = [];
      const nodeResizePatches: ResizePatch[] = [];
      const edgePatches: Array<{ id: EdgeId; patch: Partial<EdgeModel> }> = [];
      const nodeAdds: NodeModel[] = [];
      const edgeAdds: EdgeModel[] = [];
      const nodeDeletes: NodeId[] = [];
      const edgeDeletes: EdgeId[] = [];
      const drilldownPatches: Array<{
        id: DrillDownId;
        patch: Partial<DrillDownModel> | null;
      }> = [];

      for (const change of changes) {
        switch (change.type) {
          case 'create:node': {
            nodeAdds.push(change.data);
            break;
          }
          case 'create:edge': {
            edgeAdds.push(change.data);
            break;
          }
          case 'delete:node': {
            nodeDeletes.push(change.id as NodeId);
            break;
          }
          case 'delete:edge': {
            edgeDeletes.push(change.id as EdgeId);
            break;
          }
          case 'update:node': {
            nodePatches.push({ id: change.id, patch: change.data.after });
            break;
          }
          case 'update:edge': {
            edgePatches.push({ id: change.id, patch: change.data.after });
            break;
          }
          case 'move': {
            nodePatches.push({
              id: change.id,
              patch: { position: change.data.position },
            });
            break;
          }
          case 'resize': {
            nodeResizePatches.push({
              id: change.id,
              width: change.data.width,
              height: change.data.height,
              x: change.data.x,
              y: change.data.y,
            });
            break;
          }
          case 'create:drilldown': {
            drilldownPatches.push({
              id: change.id as DrillDownId,
              patch: change.data,
            });
            break;
          }
          case 'update:drilldown': {
            drilldownPatches.push({
              id: change.id as DrillDownId,
              patch: change.data.after,
            });
            break;
          }
          case 'delete:drilldown': {
            drilldownPatches.push({
              id: change.id as DrillDownId,
              patch: null,
            });
            break;
          }
          case 'create:swimlaneGroup': {
            nodeAdds.push(...change.data);
            break;
          }
          case 'update:swimlaneGroup': {
            for (const node of change.data.after) {
              nodePatches.push({ id: node.id as NodeId, patch: node });
            }
            break;
          }
          case 'delete:swimlane': {
            for (const node of change.data.after) {
              nodePatches.push({ id: node.id as NodeId, patch: node });
            }
            break;
          }
          // case 'toggle:snapToGrid': {
          //   get().meta.toggleSnapToGrid();
          //   break;
          // }
          // case 'update:settings': {
          //   get().meta.updateSettings(change.data);
          //   break;
          // }
        }
      }
      if (drilldownPatches.length) get().patchDrilldowns(drilldownPatches);
      if (nodeAdds.length) get().addNodes(nodeAdds);
      if (edgeAdds.length) get().addEdges(edgeAdds);
      if (nodeDeletes.length) get().removeNodes(nodeDeletes);
      if (edgeDeletes.length) get().removeEdges(edgeDeletes);
      if (nodePatches.length) get().patchNodes(nodePatches);
      if (nodeResizePatches.length) get().patchResizeNodes(nodeResizePatches);
      if (edgePatches.length) get().patchEdges(edgePatches);
      if (nodeChanges.length) get().applyNodesChanges(nodeChanges);
    },

    applyNodesChanges: (changes) => {
      set((s: DiagramState) => {
        for (const change of changes) {
          switch (change.type) {
            case 'select': {
              s.node.onNodeSelect(change.id as NodeId);
              return {
                rfNodes: applyNodeChanges(changes, s.rfNodes) as NodeModel[],
                nodesById: Object.fromEntries(
                  applyNodeChanges(changes, s.rfNodes).map((n) => [
                    n.id as NodeId,
                    n as NodeModel,
                  ])
                ),
              };
            }
            case 'dimensions': {
              if (
                s.nodesById[change.id].type === 'swimlane' ||
                s.nodesById[change.id].type === 'swimlaneGroup'
              ) {
                return s;
              }
              if (!change.dimensions || !change.resizing) return s;
              s.addChangesToCurrentOperation([
                {
                  type: 'resize',
                  id: change.id as NodeId,
                  status: change.resizing
                    ? ('dragging' as Status)
                    : ('complete' as Status),
                  data: {
                    width: change.dimensions.width,
                    height: change.dimensions.height,
                  },
                },
              ]);
              return {
                rfNodes: applyNodeChanges(changes, s.rfNodes) as NodeModel[],
                nodesById: Object.fromEntries(
                  applyNodeChanges(changes, s.rfNodes).map((n) => [
                    n.id as NodeId,
                    n as NodeModel,
                  ])
                ),
              };
            }
            case 'position': {
              if (!change.position || !change.dragging) return s;
              s.addChangesToCurrentOperation([
                {
                  type: 'move',
                  id: change.id as NodeId,
                  status: change.dragging
                    ? ('dragging' as Status)
                    : ('complete' as Status),
                  data: {
                    position: change.position,
                  },
                },
              ]);

              return {
                rfNodes: applyNodeChanges(changes, s.rfNodes) as NodeModel[],
                nodesById: Object.fromEntries(
                  applyNodeChanges(changes, s.rfNodes).map((n) => [
                    n.id as NodeId,
                    n as NodeModel,
                  ])
                ),
              };
            }
            case 'remove': {
              return s;
            }
            default: {
              return {
                rfNodes: applyNodeChanges(changes, s.rfNodes) as NodeModel[],
                nodesById: Object.fromEntries(
                  applyNodeChanges(changes, s.rfNodes).map((n) => [
                    n.id as NodeId,
                    n as NodeModel,
                  ])
                ),
              };
            }
          }
        }
        return s;
      });
    },
    applyEdgesChanges: (changes) => {
      for (const change of changes) {
        if (change.type === 'remove') {
          get().recordOperation([
            {
              type: 'delete:edge',
              status: 'start',
              id: change.id as EdgeId,
            } as ChangeModel,
          ]);
          break;
        }
      }
      set((s: DiagramState) => ({
        rfEdges: applyEdgeChanges(changes, s.rfEdges) as EdgeModel[],
        edgesById: Object.fromEntries(
          applyEdgeChanges(changes, s.rfEdges).map((e) => [
            e.id as EdgeId,
            e as EdgeModel,
          ])
        ),
      }));
    },
  } satisfies RfSlice);
